import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { client } from "..";
import {
  GET_MANUAL_REPORT_ROLE_CONFIG,
  GET_MY_MANUAL_REPORT_ROLE,
  GET_MY_MANUAL_REPORTS,
  GET_TEAM_MANUAL_REPORTS,
  GET_MANUAL_REPORT_PERIOD_SUMMARY,
  LIST_MANUAL_REPORT_TEAM_MEMBERS,
  UPSERT_MY_MANUAL_REPORT_ENTRY,
  UPDATE_MANUAL_REPORT_MGMT_REMARKS,
  EXPORT_MANUAL_REPORTS_EXCEL,
} from "../Queries/manualReports";

// ─── thunks ──────────────────────────────────────────────────────────────

export const fetchManualReportRoleConfig = createAsyncThunk(
  "manualReports/fetchConfig",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_MANUAL_REPORT_ROLE_CONFIG,
        fetchPolicy: "network-only",
      });
      return JSON.parse(data?.getManualReportRoleConfig?.configJson || "{}");
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchMyReportRole = createAsyncThunk(
  "manualReports/fetchMyRole",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_MY_MANUAL_REPORT_ROLE,
        fetchPolicy: "network-only",
      });
      return data?.getMyManualReportRole;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchMyReports = createAsyncThunk(
  "manualReports/fetchMyReports",
  async ({ year, monthIndex }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_MY_MANUAL_REPORTS,
        variables: { year, monthIndex },
        fetchPolicy: "network-only",
      });
      return data?.getMyManualReports || [];
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const upsertMyReportEntry = createAsyncThunk(
  "manualReports/upsertEntry",
  async ({ year, monthIndex, week, day, attendance, counts, leadIds, remarks, meetings, complaints }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPSERT_MY_MANUAL_REPORT_ENTRY,
        variables: {
          year, monthIndex, week, day,
          attendanceJson: JSON.stringify(attendance || {}),
          countsJson: JSON.stringify(counts || {}),
          leadIds: leadIds || "",
          remarks: remarks || "",
          meetings: meetings || "",
          complaints: complaints || "",
        },
      });
      return data?.upsertMyManualReportEntry;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchTeamReports = createAsyncThunk(
  "manualReports/fetchTeamReports",
  async ({ fromDate, toDate, employeeIds }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_TEAM_MANUAL_REPORTS,
        variables: { fromDate, toDate, employeeIds },
        fetchPolicy: "network-only",
      });
      return data?.getTeamManualReports || [];
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchPeriodSummary = createAsyncThunk(
  "manualReports/fetchPeriodSummary",
  async ({ fromDate, toDate, employeeIds }, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: GET_MANUAL_REPORT_PERIOD_SUMMARY,
        variables: { fromDate, toDate, employeeIds },
        fetchPolicy: "network-only",
      });
      return data?.getManualReportPeriodSummary;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const fetchTeamMembers = createAsyncThunk(
  "manualReports/fetchTeamMembers",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await client.query({
        query: LIST_MANUAL_REPORT_TEAM_MEMBERS,
        fetchPolicy: "network-only",
      });
      return data?.listManualReportTeamMembers || [];
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const updateMgmtRemarks = createAsyncThunk(
  "manualReports/updateMgmtRemarks",
  async ({ entryId, mgmtRemarks }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: UPDATE_MANUAL_REPORT_MGMT_REMARKS,
        variables: { entryId, mgmtRemarks },
      });
      return data?.updateManualReportMgmtRemarks;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

export const exportReportsExcel = createAsyncThunk(
  "manualReports/exportExcel",
  async ({ fromDate, toDate, employeeIds }, { rejectWithValue }) => {
    try {
      const { data } = await client.mutate({
        mutation: EXPORT_MANUAL_REPORTS_EXCEL,
        variables: { fromDate, toDate, employeeIds },
      });
      return data?.exportManualReportsExcel;
    } catch (err) {
      return rejectWithValue(err.message);
    }
  }
);

// ─── slice ───────────────────────────────────────────────────────────────
const manualReportsSlice = createSlice({
  name: "manualReports",
  initialState: {
    config: null,           // { roles, kpis }
    myRole: null,           // { id, employeeId, employeeName, role }
    myReports: [],          // entries for the currently selected month
    teamReports: [],        // entries for the currently filtered Team Reports query
    periodSummary: null,    // KPI rollup for Cards view
    teamMembers: [],        // list of employees in the team
    loading: false,
    error: null,
    lastSavedAt: null,
  },
  reducers: {
    clearError: (s) => { s.error = null; },
    // Optimistic local update so the form feels instant while the network upsert is in flight
    patchLocalEntry: (s, action) => {
      const { year, monthIndex, week, day, patch } = action.payload;
      const idx = s.myReports.findIndex(e => e.year === year && e.monthIndex === monthIndex && e.week === week && e.day === day);
      if (idx >= 0) s.myReports[idx] = { ...s.myReports[idx], ...patch };
      else s.myReports.push({ year, monthIndex, week, day, ...patch });
    },
  },
  extraReducers: (b) => {
    b.addCase(fetchManualReportRoleConfig.fulfilled, (s, a) => { s.config = a.payload; });
    b.addCase(fetchMyReportRole.fulfilled, (s, a) => { s.myRole = a.payload; });
    b.addCase(fetchMyReports.pending, (s) => { s.loading = true; });
    b.addCase(fetchMyReports.fulfilled, (s, a) => { s.loading = false; s.myReports = a.payload; });
    b.addCase(fetchMyReports.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
    b.addCase(upsertMyReportEntry.fulfilled, (s, a) => {
      const e = a.payload;
      if (!e) return;
      const idx = s.myReports.findIndex(x => x.year === e.year && x.monthIndex === e.monthIndex && x.week === e.week && x.day === e.day);
      if (idx >= 0) s.myReports[idx] = e; else s.myReports.push(e);
      s.lastSavedAt = Date.now();
    });
    b.addCase(fetchTeamReports.pending, (s) => { s.loading = true; });
    b.addCase(fetchTeamReports.fulfilled, (s, a) => { s.loading = false; s.teamReports = a.payload; });
    b.addCase(fetchTeamReports.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
    b.addCase(fetchPeriodSummary.fulfilled, (s, a) => { s.periodSummary = a.payload; });
    b.addCase(fetchTeamMembers.fulfilled, (s, a) => { s.teamMembers = a.payload; });
    b.addCase(updateMgmtRemarks.fulfilled, (s, a) => {
      const e = a.payload;
      if (!e) return;
      // Update in both teamReports and myReports if present
      const tIdx = s.teamReports.findIndex(x => x.id === e.id);
      if (tIdx >= 0) s.teamReports[tIdx] = e;
      const mIdx = s.myReports.findIndex(x => x.id === e.id);
      if (mIdx >= 0) s.myReports[mIdx] = e;
    });
  },
});

export const { clearError, patchLocalEntry } = manualReportsSlice.actions;
export default manualReportsSlice.reducer;
